import {
  BrokenImageOutlined,
  NoteAltOutlined,
  ReportProblemOutlined,
  RoomOutlined,
} from "@mui/icons-material";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import PolylineOutlinedIcon from "@mui/icons-material/PolylineOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import { Avatar } from "@mui/material";
import React from "react";

export const ATTRIBUTE_TYPES = {
  DEVICE_DEFICIENCY: "6a313d36-9c45-4813-a001-fb0f14f62429",
  CLINICAL_BENEFIT: "7af609de-01cf-49c4-8d3f-52951266804e",
  PERFORMANCE: "8d83844c-87d3-429f-a286-5b60525985a0",
  ADVERSE_EVENT: "3dc7dff2-2f82-45f7-85fc-782b4b7f78db",
  TARGET_LOCATION: "a811475b-d622-41af-9df1-221816fe0411",
  INDICATION_FOR_USE: "da015180-6899-423a-9bcd-72a21cc475da",
  COMORBIDITY: "4e00149a-a581-4006-8fed-1275018475d5",
  MEDICATIONS: "ed709ece-390f-48db-bc2d-cf26cd072063",
};

export enum PatientAttributeValueType {
  INVALID = "INVALID",
  STRING = "STRING",
  BOOLEAN = "BOOLEAN",
  NUMBER = "NUMBER",
  SELECT = "SELECT",
}

export const VALUE_TYPE_LABELS = {
  [PatientAttributeValueType.INVALID]: "Choose a value type",
  [PatientAttributeValueType.STRING]: "Text",
  [PatientAttributeValueType.BOOLEAN]: "True / False",
  [PatientAttributeValueType.NUMBER]: "Number",
  [PatientAttributeValueType.SELECT]: "Select",
};

export function getCategoryIcon(attributeType: PatientAttributeType) {
  const attributeTypeId = attributeType.id;
  if (attributeTypeId === ATTRIBUTE_TYPES.CLINICAL_BENEFIT)
    return <MedicalServicesOutlinedIcon />;
  if (attributeTypeId === ATTRIBUTE_TYPES.DEVICE_DEFICIENCY)
    return <BrokenImageOutlined />;
  if (attributeTypeId === ATTRIBUTE_TYPES.ADVERSE_EVENT)
    return <ReportProblemOutlined />;
  if (attributeTypeId === ATTRIBUTE_TYPES.PERFORMANCE)
    return <TrendingUpOutlinedIcon />;
  if (attributeTypeId === ATTRIBUTE_TYPES.TARGET_LOCATION)
    return <RoomOutlined />;
  if (attributeTypeId === ATTRIBUTE_TYPES.INDICATION_FOR_USE)
    return <NoteAltOutlined />;
  if (attributeTypeId === ATTRIBUTE_TYPES.COMORBIDITY)
    return <PolylineOutlinedIcon />;
  if (attributeTypeId === ATTRIBUTE_TYPES.MEDICATIONS)
    return <MedicationOutlinedIcon />;

  return (
    <Avatar className="mini-category-avatar">
      {attributeType.acronymText}
    </Avatar>
  );
}

export function getAnnexName(annex: string) {
  if (annex === "A") return "Annex A: Medical Device Problem";
  if (annex === "E")
    return "Annex E: Health Effects - Clinical Signs and Symptoms or Conditions";
  if (annex === "F") return "Annex F: Health Effects - Health Impact";
}

export function getCategoryByType(
  attributeTypes: PatientAttributeType[],
  attributeTypeId: string,
) {
  return attributeTypes.find((a) => a.id === attributeTypeId);
}

export function getCategoryName(
  attributeTypes: PatientAttributeType[],
  attribute: PatientAttribute,
) {
  const category = getCategoryByType(attributeTypes, attribute.attributeTypeId);
  return category?.name ?? "";
}

export function getFieldDataDisplayValue(
  atf: PatientAttributeTypeField,
  fieldData: PatientAttributeFieldData,
) {
  let displayValue;
  if (atf.valueType === "BOOLEAN") {
    displayValue = trueFalseOptions(
      fieldData.fieldValue,
      atf.optionValues?.split(",") ?? ["True", "False"],
    );
  } else {
    displayValue = fieldData.fieldValue;
  }
  return atf.label + ": " + displayValue;
}

export function getAttributeValue(
  imdrfEnabled: boolean,
  attributeTypes: PatientAttributeType[],
  attribute: PatientAttribute,
) {
  const category = getCategoryByType(attributeTypes, attribute.attributeTypeId);
  if (imdrfEnabled) {
    if (attribute.imdrfCode) {
      if (attribute.imdrfCode) {
        return attribute.imdrfCode.term + " - " + attribute.imdrfCode.code;
      }
    }
  }
  if (!category) {
    return attribute.attributeValue;
  }
  if (category.id === ATTRIBUTE_TYPES.DEVICE_DEFICIENCY) {
    return yesNoDeficient(attribute.attributeValue);
  }
  if (
    category.id === ATTRIBUTE_TYPES.PERFORMANCE ||
    category.id === ATTRIBUTE_TYPES.CLINICAL_BENEFIT
  ) {
    return successFail(attribute.attributeValue);
  }
  if (category.valueType === PatientAttributeValueType.BOOLEAN) {
    const options = category.optionValues.split(",");
    const value = trueFalseOptions(attribute.attributeValue, options);
    if (value !== "") {
      return value;
    }
  }
  if (category.valueType === null) {
    // display first field for custom categories
    const firstField = category.attributeTypeFields.sort(
      (a, b) => a.fieldOrder - b.fieldOrder,
    )[0];
    const firstFieldData = firstField
      ? attribute.fieldData.find(
          (d) => d.attributeTypeFieldId === firstField.id,
        )
      : undefined;
    if (!firstFieldData) return attribute.attributeValue;
    return getFieldDataDisplayValue(firstField, firstFieldData);
  }
  return attribute.attributeValue;
}

export function getOtherData(
  imdrfEnabled: boolean,
  attributeTypes: PatientAttributeType[],
  attribute: PatientAttribute,
) {
  const otherData: string[] = [];
  if (!imdrfEnabled) return otherData;

  if (attribute?.imdrfCode?.nonImdrf) {
    otherData.push("Non-IMDRF Code: " + attribute.imdrfCode.nonImdrf);
  }

  const attributeTypeId = attribute.attributeTypeId;
  const category = getCategoryByType(attributeTypes, attributeTypeId);
  if (!category) return otherData;

  if (
    category.attributeTypeFields &&
    category.attributeTypeFields.length > 0 &&
    attribute.fieldData &&
    attribute.fieldData.length > 0
  ) {
    // remove first field because it's displayed as the value
    const fieldDataList = attribute.fieldData;
    category.attributeTypeFields
      .sort((a, b) => a.fieldOrder - b.fieldOrder)
      .slice(1)
      .forEach((atf) => {
        const fieldDataRow = fieldDataList.find(
          (fd) => fd.attributeTypeFieldId === atf.id,
        );
        if (fieldDataRow) {
          const fieldData = getFieldDataDisplayValue(atf, fieldDataRow);
          otherData.push(fieldData);
        }
      });
  } else {
    if (
      category.id === ATTRIBUTE_TYPES.DEVICE_DEFICIENCY &&
      attribute.attributeValue === "true"
    ) {
      otherData.push(yesNoDeficient(attribute.attributeValue));
    } else if (
      category.id === ATTRIBUTE_TYPES.PERFORMANCE &&
      attribute.attributeValue === "false"
    ) {
      otherData.push("Sub-Category: " + successFail(attribute.attributeValue));
    }
  }
  return otherData;
}

function successFail(status: string) {
  const isTrueSet = String(status).toLowerCase() === "true";
  if (isTrueSet) return "Success";
  return "Failure";
}

function yesNoDeficient(status: string) {
  const isTrueSet = String(status).toLowerCase() === "true";
  if (isTrueSet) return "Yes (device deficient)";
  return "No (device not deficient)";
}

function trueFalseOptions(status: string, options: string[]) {
  if (!options || !options.length || options.length < 2) return "";
  const isTrueSet = String(status).toLowerCase() === "true";
  if (isTrueSet) return options[0];
  return options[1];
}
